/**
 * Owner: Masa Bayu
 * File: SearchPanel.tsx
 * Date:Time: 2026-09-18 13:18
 * Description: UI-06-S2 Search — facets · language/country search · Titles grid.
 */

import { useEffect, useState } from 'react';
import {
  listHomeTopicTiles,
  QXREAD_CORE_GENRE_SEED,
  QXREAD_CORE_PUBLICATION_TYPE_SEED,
} from '@qx/qxread-core';
import { QxEmptyState } from '../ux';
import { useLayoutClass } from '../ui01/useLayoutClass';
import { readSearchParam } from '../shell/use-shell-route';
import { DiscoveryBookGrid } from './DiscoveryBookGrid';
import {
  queryDiscoveryAuthorMatches,
  queryDiscoveryCatalog,
} from './discovery-from-catalog';
import { WorldFacetField } from './WorldFacetField';
import {
  MARKETPLACE_COUNTRY_OPTIONS,
  MARKETPLACE_LANGUAGE_OPTIONS,
} from './world-facets';
import '../home/shared/home-world-filters.css';
import './search-discovery.css';

export interface SearchPanelProps {
  readonly onNavigate: (path: string) => void;
  readonly search?: string;
}

export function SearchPanel({ onNavigate, search = '' }: SearchPanelProps) {
  const layout = useLayoutClass();
  const initialQ = readSearchParam(search, 'q');
  const [q, setQ] = useState(initialQ);
  const [author, setAuthor] = useState(readSearchParam(search, 'author') || '');
  const [topicId, setTopicId] = useState(readSearchParam(search, 'topic') || '');
  const [genreId, setGenreId] = useState('');
  const [publicationTypeId, setPublicationTypeId] = useState('');
  const [language, setLanguage] = useState(readSearchParam(search, 'language') || '');
  const [country, setCountry] = useState(readSearchParam(search, 'country') || '');

  useEffect(() => {
    setQ(readSearchParam(search, 'q'));
    const topic = readSearchParam(search, 'topic');
    if (topic.length > 0) {
      setTopicId(topic);
    }
    const authorParam = readSearchParam(search, 'author');
    if (authorParam.length > 0) {
      setAuthor(authorParam);
    }
    const languageParam = readSearchParam(search, 'language');
    if (languageParam.length > 0) {
      setLanguage(languageParam);
    }
    const countryParam = readSearchParam(search, 'country');
    if (countryParam.length > 0) {
      setCountry(countryParam);
    }
  }, [search]);

  const trimmed = q.trim();
  const authorFilter = author.length > 0 ? author : null;
  const discoveryQuery = {
    q,
    sort: 'relevance' as const,
    topicId: topicId.length > 0 ? topicId : null,
    genreId: genreId.length > 0 ? genreId : null,
    publicationTypeId: publicationTypeId.length > 0 ? publicationTypeId : null,
    language: language.length > 0 ? language : null,
    regionId: country.length > 0 ? country : null,
    author: authorFilter,
  };
  const result = queryDiscoveryCatalog(discoveryQuery);
  const authorMatches = queryDiscoveryAuthorMatches(discoveryQuery);
  const suggestions = queryDiscoveryCatalog({ sort: 'newest' });
  const hasFacets =
    topicId.length > 0 ||
    genreId.length > 0 ||
    publicationTypeId.length > 0 ||
    language.length > 0 ||
    country.length > 0 ||
    author.length > 0;
  const showEmpty = (trimmed.length > 0 || hasFacets) && result.total === 0;
  const showIdle = trimmed.length === 0 && !hasFacets;
  const showAuthors = !showIdle && authorMatches.length > 0;
  const topicTiles = listHomeTopicTiles();

  return (
    <section
      className="market-surface search-discovery"
      aria-labelledby="route-title"
      data-pw-d-surface="search"
      data-ui06-surface="discovery"
      data-ui06-s2="author-groups"
      data-qx-layout={layout}
      data-catalog-port="shared"
    >
      <header className="market-surface__hero market-surface__hero--band">
        <p className="market-surface__eyebrow">Find</p>
        <h2 id="route-title" className="market-surface__title">
          Search
        </h2>
        <p className="market-surface__support">
          Quiet discovery — query and refine. Results are catalog references — not content access ·
          not QXReader.
        </p>
      </header>

      <label className="market-search">
        <span className="visually-hidden">Search publications</span>
        <input
          type="search"
          value={q}
          onChange={(event) => {
            setQ(event.target.value);
            if (author.length > 0) {
              setAuthor('');
            }
          }}
          placeholder="Search titles, authors, topics…"
          autoComplete="off"
          aria-label="Search publications"
        />
      </label>

      <div className="search-facets" role="group" aria-label="Search filters">
        <label className="search-facets__field">
          <span className="search-facets__label">Topic</span>
          <select
            value={topicId}
            aria-label="Topic"
            onChange={(event) => {
              setTopicId(event.target.value);
            }}
          >
            <option value="">All topics</option>
            {topicTiles.map((tile) => (
              <option key={tile.topicId} value={tile.topicId}>
                {tile.label}
              </option>
            ))}
          </select>
        </label>
        <label className="search-facets__field">
          <span className="search-facets__label">Genre</span>
          <select
            value={genreId}
            aria-label="Genre"
            onChange={(event) => {
              setGenreId(event.target.value);
            }}
          >
            <option value="">Any genre</option>
            {QXREAD_CORE_GENRE_SEED.map((g) => (
              <option key={g.genreId} value={g.genreId}>
                {g.labels.en ?? g.genreId}
              </option>
            ))}
          </select>
        </label>
        <label className="search-facets__field">
          <span className="search-facets__label">Type</span>
          <select
            value={publicationTypeId}
            aria-label="Publication type"
            onChange={(event) => {
              setPublicationTypeId(event.target.value);
            }}
          >
            <option value="">Any type</option>
            {QXREAD_CORE_PUBLICATION_TYPE_SEED.map((t) => (
              <option key={t.publicationTypeId} value={t.publicationTypeId}>
                {t.labels.en ?? t.publicationTypeId}
              </option>
            ))}
          </select>
        </label>
        <WorldFacetField
          label="Language"
          anyLabel="Any language"
          searchPlaceholder="Search language…"
          options={MARKETPLACE_LANGUAGE_OPTIONS}
          value={language}
          selectAriaLabel="Language"
          searchAriaLabel="Search language"
          onChange={setLanguage}
        />
        <WorldFacetField
          label="Country"
          anyLabel="Any country"
          searchPlaceholder="Search country…"
          options={MARKETPLACE_COUNTRY_OPTIONS}
          value={country}
          selectAriaLabel="Country"
          searchAriaLabel="Search country"
          onChange={setCountry}
        />
      </div>

      <p className="search-discovery__topics-link">
        <button
          type="button"
          className="qx-btn qx-btn--ghost"
          onClick={() => {
            onNavigate('/topics');
          }}
        >
          Explore all topics
        </button>
      </p>

      {author.length > 0 ? (
        <p className="search-author-active" role="status">
          Author: <strong>{author}</strong>
          <button
            type="button"
            className="qx-btn qx-btn--ghost search-author-active__clear"
            onClick={() => {
              setAuthor('');
            }}
          >
            Clear
          </button>
        </p>
      ) : null}

      <p className="market-toolbar__meta market-search__status" role="status">
        {showIdle
          ? 'Type to search the public catalog'
          : `${result.total} result${result.total === 1 ? '' : 's'}`}
      </p>

      {showEmpty ? (
        <QxEmptyState
          title="No publications"
          description="Try different filters, or browse Topics and Discover."
          actionLabel="Explore Topics"
          onAction={() => {
            onNavigate('/topics');
          }}
        />
      ) : showIdle ? (
        <div className="market-suggest">
          <h3 className="market-suggest__title">Suggested titles</h3>
          <p className="market-search__hint">
            Tip: try an author name, “Relativity”, or pick a Topic facet above.
          </p>
          <DiscoveryBookGrid
            cards={suggestions.cards.slice(0, 4)}
            onNavigate={onNavigate}
            listLabel="Suggested titles"
            idPrefix="search-suggest"
          />
        </div>
      ) : (
        <>
          {showAuthors ? (
            <section className="search-authors" aria-labelledby="search-authors-title">
              <h3 id="search-authors-title" className="search-authors__title">
                Authors
              </h3>
              <ul className="search-authors__list" aria-label="Author matches">
                {authorMatches.map((match) => (
                  <li key={match.name}>
                    <button
                      type="button"
                      className={
                        author === match.name
                          ? 'search-authors__btn is-active'
                          : 'search-authors__btn'
                      }
                      onClick={() => {
                        setAuthor(match.name);
                        setQ('');
                      }}
                    >
                      <span className="search-authors__name">{match.name}</span>
                      <span className="search-authors__count">
                        {match.titleCount} title{match.titleCount === 1 ? '' : 's'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <DiscoveryBookGrid
            cards={result.cards}
            onNavigate={onNavigate}
            listLabel="Search results"
            idPrefix="search"
          />
        </>
      )}
    </section>
  );
}
